import {
  isTrackerControlNote,
  isTrackerPitchNote,
  TRACKER_NOTE_COUNT,
  TRACKER_NOTE_CUT,
  TRACKER_NOTE_EMPTY,
  TRACKER_NOTE_HIGHEST_OCTAVE,
  TRACKER_NOTE_LOWEST_OCTAVE,
  TRACKER_NOTE_OFF,
  type TrackerPitchMode,
  TRACKER_PITCH_MODE
} from "./model.js";

export const TRACKER_KEYBOARD_MIN_OCTAVE = 2;
export const TRACKER_KEYBOARD_MAX_OCTAVE = 6;
export const TRACKER_FINE_20_MAX_NOTE = 67;

const NOTE_NAMES = ["C-", "C#", "D-", "D#", "E-", "F-", "F#", "G-", "G#", "A-", "A#", "B-"];

const COARSE_16_FREQUENCIES = [
  89, 95, 100, 106, 113, 119, 126, 134, 142, 150, 159, 169, 179, 189, 200, 212, 225, 238, 253, 268, 284, 300,
  318, 337, 357, 378, 401, 425, 450, 477, 505, 535, 567, 601, 636, 674, 714, 757, 802, 850, 900, 954, 1010,
  1070, 1134, 1201, 1273, 1349, 1429, 1514, 1604, 1699, 1800, 1907, 2021, 2141, 2268, 2403, 2546, 2697,
  2858, 3028, 3208, 3398, 3600, 3815, 4041, 4282, 4536, 4806, 5092, 5395
];

const BALANCED_19_FREQUENCIES = [
  714, 757, 802, 850, 900, 954, 1010, 1070, 1134, 1201, 1273, 1349, 1429, 1514, 1604, 1699, 1800, 1907, 2021,
  2141, 2268, 2403, 2546, 2697, 2858, 3028, 3208, 3398, 3600, 3815, 4041, 4282, 4536, 4806, 5092, 5395, 5715,
  6055, 6415, 6797, 7201, 7629, 8083, 8563, 9072, 9612, 10184, 10789, 11431, 12110, 12830, 13593, 14402,
  15258, 16165, 17127, 18145, 19224, 20367, 21578, 22861, 24221, 25661, 27187, 28803, 30516, 32331, 34253,
  36290, 38448, 40734, 43156
];

const FINE_20_FREQUENCIES = [
  1429, 1514, 1604, 1699, 1800, 1907, 2021, 2141, 2268, 2403, 2546, 2697, 2858, 3028, 3208, 3398, 3600, 3815,
  4041, 4282, 4536, 4806, 5092, 5395, 5715, 6055, 6415, 6797, 7201, 7629, 8083, 8563, 9072, 9612, 10184,
  10789, 11431, 12110, 12830, 13593, 14402, 15258, 16165, 17127, 18145, 19224, 20367, 21578, 22861, 24221,
  25661, 27187, 28803, 30516, 32331, 34253, 36290, 38448, 40734, 43156, 45722, 48441, 51322, 54373, 57607,
  61032, 64661, 0, 0, 0, 0, 0
];

const NOTE_KEY_TO_OFFSET: Record<string, number> = {
  KeyZ: 0,
  KeyS: 1,
  KeyX: 2,
  KeyD: 3,
  KeyC: 4,
  KeyV: 5,
  KeyG: 6,
  KeyB: 7,
  KeyH: 8,
  KeyN: 9,
  KeyJ: 10,
  KeyM: 11,
  KeyQ: 12,
  Digit2: 13,
  KeyW: 14,
  Digit3: 15,
  KeyE: 16,
  KeyR: 17,
  Digit5: 18,
  KeyT: 19,
  Digit6: 20,
  KeyY: 21,
  Digit7: 22,
  KeyU: 23
};

export function clampKeyboardOctave(value: number): number {
  return Math.max(TRACKER_KEYBOARD_MIN_OCTAVE, Math.min(TRACKER_KEYBOARD_MAX_OCTAVE, Math.round(value)));
}

export function maxNoteForPitchMode(pitchMode: TrackerPitchMode): number {
  switch (pitchMode) {
    case TRACKER_PITCH_MODE.fine20:
      return TRACKER_FINE_20_MAX_NOTE;
    default:
      return TRACKER_NOTE_COUNT;
  }
}

export function isNoteValidForPitchMode(noteCode: number, pitchMode: TrackerPitchMode): boolean {
  if (noteCode === TRACKER_NOTE_EMPTY || isTrackerControlNote(noteCode)) {
    return true;
  }
  if (!isTrackerPitchNote(noteCode)) {
    return false;
  }
  return noteCode <= maxNoteForPitchMode(pitchMode);
}

export function noteCodeToCompactLabel(noteCode: number): string {
  return noteCodeToLabel(noteCode).replace("-", "");
}

export function pitchModeDisplayName(pitchMode: TrackerPitchMode): string {
  switch (pitchMode) {
    case TRACKER_PITCH_MODE.coarse16:
      return "Coarse_16";
    case TRACKER_PITCH_MODE.fine20:
      return "Fine_20";
    default:
      return "Balanced_19";
  }
}

export function pitchModeRangeLabel(pitchMode: TrackerPitchMode): string {
  return `${noteCodeToCompactLabel(1)}..${noteCodeToCompactLabel(maxNoteForPitchMode(pitchMode))}`;
}

export function makeNoteCode(octave: number, semitone: number, pitchMode?: TrackerPitchMode): number | null {
  const absoluteOctave = clampKeyboardOctave(octave) + Math.floor(semitone / 12);
  const normalizedSemitone = ((semitone % 12) + 12) % 12;
  if (absoluteOctave < TRACKER_NOTE_LOWEST_OCTAVE || absoluteOctave > TRACKER_NOTE_HIGHEST_OCTAVE) {
    return null;
  }
  const noteCode = (absoluteOctave - TRACKER_NOTE_LOWEST_OCTAVE) * 12 + normalizedSemitone + 1;
  if (pitchMode !== undefined && !isNoteValidForPitchMode(noteCode, pitchMode)) {
    return null;
  }
  return noteCode;
}

export function trackerNoteForKeyCode(code: string, octave: number, pitchMode?: TrackerPitchMode): number | null {
  const semitone = NOTE_KEY_TO_OFFSET[code];
  if (semitone === undefined) {
    return null;
  }
  return makeNoteCode(octave, semitone, pitchMode);
}

export function noteCodeToLabel(noteCode: number): string {
  if (noteCode === TRACKER_NOTE_EMPTY) {
    return "---";
  }
  if (noteCode === TRACKER_NOTE_OFF) {
    return "OFF";
  }
  if (noteCode === TRACKER_NOTE_CUT) {
    return "CUT";
  }
  const index = noteCode - 1;
  if (index < 0 || index >= TRACKER_NOTE_COUNT) {
    return "???";
  }
  const octave = TRACKER_NOTE_LOWEST_OCTAVE + Math.floor(index / 12);
  const name = NOTE_NAMES[index % 12];
  return `${name}${octave}`;
}

export function noteCodeToFrequencyRegister(noteCode: number, pitchMode: TrackerPitchMode): number {
  if (noteCode === TRACKER_NOTE_EMPTY || isTrackerControlNote(noteCode)) {
    return 0;
  }
  if (!isNoteValidForPitchMode(noteCode, pitchMode)) {
    return 0;
  }
  const index = noteCode - 1;
  switch (pitchMode) {
    case TRACKER_PITCH_MODE.coarse16:
      return COARSE_16_FREQUENCIES[index] ?? 0;
    case TRACKER_PITCH_MODE.fine20:
      return FINE_20_FREQUENCIES[index] ?? 0;
    default:
      return BALANCED_19_FREQUENCIES[index] ?? 0;
  }
}

export function allNoteOptions(pitchMode: TrackerPitchMode, selectedNote?: number): Array<{ code: number; label: string }> {
  const options = [
    { code: TRACKER_NOTE_EMPTY, label: "---" },
    { code: TRACKER_NOTE_OFF, label: "OFF" },
    { code: TRACKER_NOTE_CUT, label: "CUT" }
  ];
  for (let code = 1; code <= maxNoteForPitchMode(pitchMode); code += 1) {
    options.push({ code, label: noteCodeToLabel(code) });
  }
  if (
    selectedNote !== undefined
    && selectedNote !== TRACKER_NOTE_EMPTY
    && !isTrackerControlNote(selectedNote)
    && !isNoteValidForPitchMode(selectedNote, pitchMode)
    && selectedNote >= 1
    && selectedNote <= TRACKER_NOTE_COUNT
  ) {
    options.push({
      code: selectedNote,
      label: `${noteCodeToLabel(selectedNote)} (invalid in ${pitchModeDisplayName(pitchMode)})`
    });
  }
  return options;
}
